import { useEffect, useRef, useState } from 'react';
import { ThreeDotsSvg } from '../Svgs';
import './AllFiles.scss';
import useFiles from '../../../../hooks/useFiles';
import ShimmerMenuList from '../../../../../CommonComponents/ShimmerEffectDesign/ShimmerMenu';
import { formatFileSize } from '../../../../../../utils/commonFunctions';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';

type AllFilesProps = {
    type: string;
    allFiles?: any[] | null;
    isReadOnlyActions?: boolean;
    setActionFileId: (value: { status: string | null, fileId: string | null }) => void;
    setTypeOfAction: (value: 'delete' | 'cancel' | 'pause' | 'resume') => void;
    selectedPayload: string | null;
};

const AllFiles: React.FC<AllFilesProps> = ({ type, allFiles, isReadOnlyActions = false, setTypeOfAction, setActionFileId, selectedPayload }) => {
    const [rowMenuOpen, setRowMenuOpen] = useState<any | null>(null)
    const threeDotsRef = useRef(null)
    const menuRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const { isGroundFilesLoading, downloadFileToLocal } = useFiles();

    //HANDLE THREE DOTS OPENING AND CLOSING
    useEffect(() => {
        const clickOutside = (e: MouseEvent) => {
            const allMenus = document.querySelectorAll('.three-dots-allfiles');
            const menu = rowMenuOpen !== null ? menuRefs.current[rowMenuOpen?.uniqueId] : null;
            if (menu && menu.contains(e.target as Node)) return;

            for (let i = 0; i < allMenus.length; i++) {
                if (allMenus[i].contains(e.target as Node)) {
                    return; // Clicked inside any of the menus
                }
            }
            setRowMenuOpen(null); // Clicked outside all menus
        };

        document.addEventListener('mousedown', clickOutside);
        return () => {
            document.removeEventListener('mousedown', clickOutside);
        };
    }, [rowMenuOpen]);

    //HANDLE THREEDOTS OPEN FOR PARTICULAR ROW
    const handleRowMenuOpen = (e, file: any) => {
        if (isReadOnlyActions) return;
        e.stopPropagation();
        if (file?.fileStatus === 'Suspend Inprogress' || file?.fileStatus === 'Resume Inprogress') {
            setRowMenuOpen(null);
        } else {
            setRowMenuOpen(prev => (prev?.uniqueId === file?.uniqueId ? null : file));
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setMenuPosition({ top: rect.bottom + window.scrollY - 90, left: rect.right - 130 + window.scrollX });
        }
    };

    //PLAN UPLINK -> IN FUTURE
    const handlePlanUplink = (e) => {
        e.stopPropagation()
        e.preventDefault()
        setRowMenuOpen(null)

    }

    //CANCEL ONGOING UPLOAD TO SATELLITE
    const handleCancel = (file) => {
        setActionFileId({ status: file?.fileStatus, fileId: file?.uniqueId })
        setTypeOfAction('cancel')
    }

    //DOWNLOAD FILE TO LOCAL COMPUTER
    const handleDownload = (file) => {
        downloadFileToLocal(file?.uniqueId)
    }

    //PAUSE ONGOING UPLOAD TO SATELLITE
    const handlePause = (file) => {
        setActionFileId({ status: file?.fileStatus, fileId: file?.uniqueId })
        setTypeOfAction('pause')
    }

    //RESUME ONGOING UPLOAD TO SATELLITE
    const handleResume = (file) => {
        setActionFileId({ status: file?.fileStatus, fileId: file?.uniqueId })
        setTypeOfAction('resume')
    }

    //DELETE FILE FROM MINIO STORAGE
    const handleDeleteFileFromStorage = (e, file) => {
        e.preventDefault()
        setActionFileId({ status: file?.fileStatus, fileId: file?.uniqueId })
        setTypeOfAction('delete')
        setRowMenuOpen(null)
    }

    const NO_TOOLTIP_STATUSES = [
        "Suspended",
        "Error",
        "Uplinking",
        "Suspend Inprogress",
        "Resume Inprogress",
        "Completed",
        "Cancelled",
        "Pending",
        "success" //status for downloads file
    ];

    const DELETE_VISIBLE_STATUSES = [
        "Suspended",
        "Error",
        "Completed",
        "Cancelled",
        "Pending",
        "success",
    ];

    return (
        <div className='allfiles-wrapper ps-4'>
            <div className="allfiles-table">
                {/* HEADING OF THE TABLE */}
                <div className="allfiles-table-header">
                    <span className={`${selectedPayload === 'downloads' ? 'w-75' : 'w-55'} allfiles-table-header-text`}>Name</span>
                    <span className={`${selectedPayload === 'downloads' ? 'w-20' : 'w-25'} allfiles-table-header-text`}>File Size</span>
                    {selectedPayload !== 'downloads' && <span className='w-15 allfiles-table-header-text'>Uplink Status</span>}
                    <span className='w-5 allfiles-table-header-text'></span>
                </div>
                {/* BODY OF THE TABLE */}
                <div className="allfiles-table-body gap-1">
                    {!isGroundFilesLoading ? (
                        allFiles?.length ? (
                            <>
                                {allFiles.map((file, index) => (
                                    <div key={file.uniqueId} className="position-relative">
                                        <div className="allfiles-table-body-row">
                                            <div className={`${selectedPayload === 'downloads' ? 'w-75' : 'w-55'} d-flex gap-2 allfiles-table-row-text`}>
                                                <div className="allfiles-filename-wrapper">
                                                    <CustomToolTip title={file?.fileName ?? ''} placement="top">
                                                        <span className="text-truncate d-block">
                                                            {file?.fileName}
                                                        </span>
                                                    </CustomToolTip>
                                                </div>
                                            </div>

                                            <span className={`${selectedPayload === 'downloads' ? 'w-20' : 'w-25'} allfiles-table-row-text-file-size`}>
                                                {formatFileSize(file?.fileSize ?? 0)}
                                            </span>

                                            {selectedPayload !== 'downloads' && <span
                                                className={`w-15 allfiles-table-row-text ${file?.fileStatus}`}
                                            >
                                                {file?.fileStatus === "Suspend Inprogress"
                                                        ? "Pausing"
                                                        : file?.fileStatus === "Resume Inprogress"
                                                            ? "Resuming"
                                                            : file?.fileStatus === 'Suspended'
                                                                ? "Paused"
                                                                : file?.fileStatus === 'Error'
                                                                    ? "Failed"
                                                                    : file?.fileStatus}
                                            </span>}

                                            <span
                                                role="button"
                                                className={`w-5 d-flex justify-content-center align-items-center cursor-pointer three-dots-allfiles ${file?.fileStatus === "Suspend Inprogress" || file?.fileStatus === "Resume Inprogress"
                                                    ? "disable"
                                                    : ""
                                                    }`}
                                                style={{ position: "relative" }}
                                                onClick={(e) => handleRowMenuOpen(e, file)}
                                                ref={threeDotsRef}
                                            >
                                                {file?.fileStatus === "Suspend Inprogress" || file?.fileStatus === "Resume Inprogress" ? (
                                                    <CustomToolTip
                                                        title={
                                                            file?.fileStatus === "Suspend Inprogress"
                                                                ? "Pausing File Uplink"
                                                                : "Resuming File Uplink"
                                                        }
                                                        placement="top"
                                                    >
                                                        <ThreeDotsSvg />
                                                    </CustomToolTip>
                                                ) : (
                                                        NO_TOOLTIP_STATUSES.includes(file?.fileStatus) && file?.fileStatus !== ""
                                                            ? <span className={isReadOnlyActions ? 'opacity-50' : ''}><ThreeDotsSvg /></span>
                                                            : ''
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                {/* Floating menu outside the map */}
                                <div
                                    ref={(el) => {
                                        if (rowMenuOpen) {
                                            menuRefs.current[rowMenuOpen?.uniqueId] = el;
                                        }
                                    }}
                                    className={`three-dots-menu ${!isReadOnlyActions && rowMenuOpen?.uniqueId ? "active" : ""}`}
                                    style={{ width: "82px", top: menuPosition.top, left: menuPosition.left }}
                                >
                                    {rowMenuOpen?.fileStatus === "Uplinking" && (
                                        <span role="button" className="three-dots-menu-options" onClick={() => handlePause(rowMenuOpen)}>
                                            <span className='ms-2'>Pause</span>
                                        </span>
                                    )}
                                    {rowMenuOpen?.fileStatus === "Suspended" && (
                                        <span role="button" className="three-dots-menu-options" onClick={() => handleResume(rowMenuOpen)}>
                                            <span className='ms-2'>Resume</span>
                                        </span>
                                    )}
                                    {(rowMenuOpen?.fileStatus === "Suspended" || rowMenuOpen?.fileStatus === "Uplinking") && (
                                        <span role="button" className="three-dots-menu-options" onClick={() => handleCancel(rowMenuOpen)}>
                                            <span className='ms-2'>Cancel</span>
                                        </span>
                                    )}
                                    {DELETE_VISIBLE_STATUSES.includes(rowMenuOpen?.fileStatus) && (
                                            <span
                                                role="button"
                                                className="three-dots-menu-options"
                                                onClick={(e) => handleDeleteFileFromStorage(e, rowMenuOpen)}
                                            >
                                                <span className='ms-2'>Delete</span>
                                            </span>
                                        )}
                                    {(rowMenuOpen?.fileStatus === "success" && selectedPayload === 'downloads') && (
                                        <span role="button" className="three-dots-menu-options" onClick={() => handleDownload(rowMenuOpen)}>
                                            <span className='ms-2'>Download</span>
                                        </span>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="no-files-text">{selectedPayload === 'downloads' ? 'No downloaded files available' : 'No ground files available'}</div>
                        )
                    ) : (
                        <ShimmerMenuList />
                    )}
                </div>
            </div>
        </div>

    )
}

export default AllFiles;